'use client';

import type { ReactNode } from 'react';
import { CheckCircle2Icon, CircleHelpIcon, Loader2Icon, PauseIcon } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { CARD, HINT, MUTED } from '@/components/ui';

/**
 * 任务 UI 组件集。
 *
 * 照搬搬小书 `xhshub/apps/web/src/app/mobile/task-ui.tsx`（结构、props、状态文案一致），
 * 只把它的品牌色 token 换成本站的黑白灰：success/warning → foreground/muted-foreground。
 */

/** 任务状态文案基线 */
export const TASK_STATUS_LABELS: Record<string, string> = {
  idle: '未开始',
  pending: '等待中',
  running: '进行中',
  paused: '已暂停',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
};

/** 取状态文案；页面可用 overrides 保留自己的措辞（如迁移的「迁移中」） */
export function taskStatusLabel(
  status: string,
  overrides?: Record<string, string>,
): string | undefined {
  return overrides?.[status] ?? TASK_STATUS_LABELS[status];
}

/** 是否进行中（idle / running / paused） */
export function isActiveTaskStatus(status: string): boolean {
  return status === 'idle' || status === 'running' || status === 'paused';
}

/** 统一的进度条 */
export function ProgressBar({
  done = 0,
  total = 0,
  ratio,
  className = 'mt-2',
}: {
  done?: number;
  total?: number;
  /** 直接给比例（0~1），优先于 done/total */
  ratio?: number;
  /** 外层间距（默认 mt-2） */
  className?: string;
}) {
  const percent = ratio != null ? ratio * 100 : total > 0 ? (done / total) * 100 : 0;
  return (
    <div className={`bg-muted h-1.5 overflow-hidden rounded-full ${className}`}>
      <div
        className="bg-primary h-full rounded-full transition-all"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

/**
 * 任务进度卡（运行中 / 已完成二合一）。
 */
export function TaskProgressCard({
  title,
  right,
  detail,
  current,
  note,
  extra,
  done,
  total,
  ratio,
  icon,
  action,
  finished = false,
  /** 暂停中：不再转圈，改用暂停图标 */
  paused = false,
  bar = true,
}: {
  title: ReactNode;
  /** 标题右侧（如「12 / 30」） */
  right?: ReactNode;
  detail?: ReactNode;
  current?: string | null;
  note?: ReactNode;
  extra?: ReactNode;
  done?: number;
  total?: number;
  /** 直接给比例（0~1），优先于 done/total */
  ratio?: number;
  /** 覆盖默认图标（默认：进行中 Loader、完成 CheckCircle） */
  icon?: ReactNode;
  /** 条下方的内容（按钮 / 统计行） */
  action?: ReactNode;
  finished?: boolean;
  paused?: boolean;
  /** 完成态默认不显示进度条 */
  bar?: boolean;
}) {
  return (
    <Card className={CARD}>
      <div className="flex items-center gap-2">
        {icon ??
          (finished ? (
            <CheckCircle2Icon className="size-5 text-foreground" />
          ) : paused ? (
            <PauseIcon className="text-muted-foreground size-5" />
          ) : (
            <Loader2Icon className="text-primary size-4 animate-spin" />
          ))}
        <span className="text-foreground flex-1 text-callout font-semibold ios:text-body">{title}</span>
        {right != null ? (
          <span className="text-muted-foreground shrink-0 text-footnote tabular-nums">{right}</span>
        ) : null}
      </div>
      {detail != null ? <p className={`${MUTED} ${'tabular-nums'}`}>{detail}</p> : null}
      {extra}
      {current ? <p className={`${MUTED} truncate`}>{current}</p> : null}
      {note != null ? <p className={HINT}>{note}</p> : null}
      {bar ? <ProgressBar done={done} total={total} ratio={ratio} /> : null}
      {action}
    </Card>
  );
}

/** 「Card + 转圈 + 文案」的加载卡 */
export function LoadingCard({ children }: { children: ReactNode }) {
  return (
    <Card className={CARD}>
      <div className="flex items-center gap-2">
        <Loader2Icon className="text-primary size-5 animate-spin" />
        <span className={MUTED}>{children}</span>
      </div>
    </Card>
  );
}

/** 带图标前缀的提示行 */
export function HintLine({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground mt-2 flex items-start gap-2 text-xs">
      <CircleHelpIcon size={14} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** 内联加载行（弹窗/区块里用，不带卡片）：转圈 + 文案 */
export function LoadingRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-4">
      <Loader2Icon className="text-primary size-5 animate-spin" />
      <span className={MUTED}>{children}</span>
    </div>
  );
}

/** 页面顶部的「说明」/提示卡 */
export function InfoCard({
  children,
  title = '说明',
  icon,
  action,
}: {
  children?: ReactNode;
  title?: string;
  icon?: ReactNode;
  /** 卡底部的操作（如「重新加载」按钮） */
  action?: ReactNode;
}) {
  return (
    <Card className={CARD}>
      <p className="text-foreground flex items-center gap-2 text-sm font-semibold ios:text-body">
        {icon}
        {title}
      </p>
      <div className={`${MUTED} ios:text-subheadline`}>{children}</div>
      {action}
    </Card>
  );
}
